import React from 'react';
import { parseDictEntry } from '../firebase/dictionaries';

const LAYERING_OPTIONS = ['+', '/', '//'];

function parseRockComposite(short) {
  if (!short) return { rockShort: '', soilShort: '' };
  const rock = short.match(/^([^(]+)/);
  const soil = short.match(/\(([^)]+)\)/);
  return {
    rockShort: rock ? rock[1].trim() : '',
    soilShort: soil ? soil[1].trim() : '',
  };
}
function parseRockCompositeFull(full) {
  if (!full) return { rockFull: '', soilFull: '' };
  const m = full.match(/^([^(]+)(?:\s*\(([^)]+)\))?/);
  return {
    rockFull: (m?.[1] || '').trim(),
    soilFull: (m?.[2] || '').trim(),
  };
}
function parseSubComposite(s) {
  const empty = { p1: '', sep1: '', p2: '', sep2: '', p3: '' };
  if (!s) return empty;
  const m = s.match(/^(.+?)(?:\s*(\+|\/\/|\/)\s*(.+?)(?:\s*(\+|\/\/|\/)\s*(.+))?)?$/);
  if (!m) return { ...empty, p1: s.trim() };
  return {
    p1: (m[1] || '').trim(),
    sep1: m[2] || '',
    p2: (m[3] || '').trim(),
    sep2: m[4] || '',
    p3: (m[5] || '').trim(),
  };
}
function buildSubComposite(p1, sep1, p2, sep2, p3) {
  if (!p1) return '';
  let out = p1;
  if (p2) out += (sep1 ? ` ${sep1} ` : ' ') + p2;
  if (p3) out += (sep2 ? ` ${sep2} ` : ' ') + p3;
  return out;
}

/**
 * Formularz litologii — wyodrębniony z LayerEditDialog, używany też w panelu rozwiniętym
 * inline w tabeli warstw. Działa identycznie w obu miejscach.
 *
 * Props:
 *  - value: obiekt layer-like z polami { materialType, lithologyRockSoil01..03,
 *           lithologyRockSoilFullName01..03, layeringRockSoil01..02, lithologyRock01 }
 *  - onChange(patch): bulk update — przekazuje obiekt z nowymi polami
 *  - lithoDict, rockDict, rockSubDict: dane słownikowe z useLithologyDictionaries
 *  - compact (boolean): jeśli true, ograniczony layout (mniej miejsca)
 */
export default function LithologyForm({
  value,
  onChange,
  lithoDict = [],
  rockDict = [],
  rockSubDict = [],
  compact = false,
}) {
  const isRock = (value.materialType || 'grunt') === 'skala';
  const subDict = rockSubDict;

  const lookupFullByShort = (short) => {
    if (!short) return '';
    const found = rockSubDict.find(e => e.shortForm === short)
      || rockDict.find(e => e.shortForm === short)
      || lithoDict.find(e => e.shortForm === short);
    return found?.fullName || '';
  };

  // Tryb Grunt — proste sloty 01/02/03
  const setSlot = (slot, fullString) => {
    const { shortForm, fullName } = parseDictEntry(fullString);
    if (slot === 1) onChange({ lithologyRockSoil01: shortForm, lithologyRockSoilFullName01: fullName });
    if (slot === 2) onChange({ lithologyRockSoil02: shortForm, lithologyRockSoilFullName02: fullName });
    if (slot === 3) onChange({ lithologyRockSoil03: shortForm, lithologyRockSoilFullName03: fullName });
  };

  // Tryb Skała — każdy slot composite "rock(sub1 sep1 sub2 sep2 sub3)"
  const parseSlotFull = (slotIdx) => {
    const short = value[`lithologyRockSoil0${slotIdx}`] || '';
    const full = value[`lithologyRockSoilFullName0${slotIdx}`] || '';
    const { rockShort } = parseRockComposite(short);
    let { rockFull } = parseRockCompositeFull(full);
    if (rockShort && !rockFull) rockFull = lookupFullByShort(rockShort);
    const soilCompShort = short.match(/\(([^)]+)\)/)?.[1] || '';
    const soilCompFull = full.match(/\(([^)]+)\)/)?.[1] || '';
    const ss = parseSubComposite(soilCompShort);
    const sf = parseSubComposite(soilCompFull);
    const healSub = (s, f) => (s && !f) ? lookupFullByShort(s) : f;
    return {
      rockShort, rockFull,
      sub1: { short: ss.p1, full: healSub(ss.p1, sf.p1) },
      sep1: ss.sep1,
      sub2: { short: ss.p2, full: healSub(ss.p2, sf.p2) },
      sep2: ss.sep2,
      sub3: { short: ss.p3, full: healSub(ss.p3, sf.p3) },
    };
  };

  const rebuildSlot = (slotIdx, parts) => {
    const { rockShort, rockFull, sub1, sep1, sub2, sep2, sub3 } = parts;
    const soilCompShort = buildSubComposite(sub1.short, sep1, sub2.short, sep2, sub3.short);
    const soilCompFull = buildSubComposite(sub1.full, sep1, sub2.full, sep2, sub3.full);
    let composite = '';
    let compositeFull = '';
    if (rockShort) {
      composite = soilCompShort ? `${rockShort}(${soilCompShort})` : rockShort;
      compositeFull = soilCompFull ? `${rockFull} (${soilCompFull})` : rockFull;
    }
    const patch = {
      [`lithologyRockSoil0${slotIdx}`]: composite,
      [`lithologyRockSoilFullName0${slotIdx}`]: compositeFull,
    };
    if (slotIdx === 1) patch.lithologyRock01 = rockShort;
    onChange(patch);
  };

  const setRockPart = (slotIdx, fullString) => {
    const { shortForm, fullName } = parseDictEntry(fullString);
    const cur = parseSlotFull(slotIdx);
    rebuildSlot(slotIdx, { ...cur, rockShort: shortForm, rockFull: fullName });
  };
  const setSubSlot = (slotIdx, subIdx, fullString) => {
    const { shortForm, fullName } = parseDictEntry(fullString);
    const cur = parseSlotFull(slotIdx);
    const newCur = { ...cur };
    newCur[`sub${subIdx}`] = { short: shortForm, full: fullName };
    rebuildSlot(slotIdx, newCur);
  };
  const setSubSep = (slotIdx, sepIdx, sep) => {
    const cur = parseSlotFull(slotIdx);
    const newCur = { ...cur };
    newCur[`sep${sepIdx}`] = sep;
    rebuildSlot(slotIdx, newCur);
  };

  const switchMaterialType = (newType) => onChange({ materialType: newType });

  // Wartość <select> musi być IDENTYCZNA ze stringiem opcji, inaczej przeglądarka
  // pokaże "-- wybierz --" mimo że dane są ustawione. Nie da się jej skleić z pól
  // warstwy, bo opis w danych i w słowniku różni się wielkością liter
  // ("Pd - piasek drobny" w warstwie vs "Pd - Piasek drobny" w słowniku).
  // Dlatego szukamy wpisu po samym skrócie i bierzemy jego fullString.
  const slotValue = (short, full, dict = []) => {
    if (!short) return '';
    const entry = dict.find(e => e.shortForm === short);
    if (entry) return entry.fullString;
    // Skrót spoza słownika (np. Gb, T, Pd(g)) — zachowujemy to co w danych,
    // a brakującą opcję dorobi optionsWithCurrent().
    return full ? `${short} - ${full}` : short;
  };

  // Dokłada opcję dla wartości, której nie ma w słowniku — żeby aktualna litologia
  // była widoczna i nie zniknęła przy zapisie.
  const optionsWithCurrent = (dict, currentValue) => {
    const opts = dict.map(e => ({ value: e.fullString, label: e.fullString }));
    if (currentValue && !dict.some(e => e.fullString === currentValue)) {
      opts.unshift({ value: currentValue, label: `${currentValue}  (spoza słownika)` });
    }
    return opts;
  };

  const slot1Value = slotValue(value.lithologyRockSoil01, value.lithologyRockSoilFullName01, lithoDict);
  const slot2Value = slotValue(value.lithologyRockSoil02, value.lithologyRockSoilFullName02, lithoDict);
  const slot3Value = slotValue(value.lithologyRockSoil03, value.lithologyRockSoilFullName03, lithoDict);

  const rockSlots = [1, 2, 3].map(i => {
    const s = parseSlotFull(i);
    return {
      idx: i,
      rockValue: slotValue(s.rockShort, s.rockFull, rockDict),
      sub1Value: slotValue(s.sub1.short, s.sub1.full, subDict),
      sub2Value: slotValue(s.sub2.short, s.sub2.full, subDict),
      sub3Value: slotValue(s.sub3.short, s.sub3.full, subDict),
      sep1: s.sep1,
      sep2: s.sep2,
    };
  });

  const sm = compact ? 'text-xs' : 'text-sm';
  const lbl = compact ? 'text-[10px]' : 'text-xs';
  const ipy = compact ? 'py-1' : 'py-1.5';

  return (
    <>
      <div className={compact ? 'mb-2' : 'mb-4'}>
        <label className={`block ${compact ? 'text-[10px] font-semibold uppercase' : 'text-sm font-semibold'} text-gray-700 mb-${compact ? '1' : '2'}`}>Litologia:</label>
        <div className="flex gap-4 mb-2">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="radio" checked={!isRock} onChange={() => switchMaterialType('grunt')} />
            <span className={sm}>Grunt</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="radio" checked={isRock} onChange={() => switchMaterialType('skala')} />
            <span className={sm}>Skała</span>
          </label>
        </div>

        {!isRock && (
          <div className="space-y-2">
            <div>
              <label className={`block ${lbl} text-gray-500 mb-1`}>Litologia Gruntu 1:</label>
              <select value={slot1Value} onChange={(e) => setSlot(1, e.target.value)}
                className={`border border-gray-300 rounded px-2 ${ipy} w-full ${sm} outline-none`}
                disabled={lithoDict.length === 0}>
                <option value="">-- wybierz --</option>
                {optionsWithCurrent(lithoDict, slot1Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-[80px_1fr] gap-2 items-end">
              <div>
                <label className={`block ${lbl} text-gray-500 mb-1`}>Przew. 1:</label>
                <select value={value.layeringRockSoil01 || ''} onChange={(e) => onChange({ layeringRockSoil01: e.target.value })}
                  className={`border border-gray-300 rounded px-2 ${ipy} w-full ${sm} outline-none`}>
                  <option value="">--</option>
                  {LAYERING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={`block ${lbl} text-gray-500 mb-1`}>Litologia Gruntu 2:</label>
                <select value={slot2Value} onChange={(e) => setSlot(2, e.target.value)}
                  className={`border border-gray-300 rounded px-2 ${ipy} w-full ${sm} outline-none`}
                  disabled={lithoDict.length === 0}>
                  <option value="">-- wybierz --</option>
                  {optionsWithCurrent(lithoDict, slot2Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-[80px_1fr] gap-2 items-end">
              <div>
                <label className={`block ${lbl} text-gray-500 mb-1`}>Przew. 2:</label>
                <select value={value.layeringRockSoil02 || ''} onChange={(e) => onChange({ layeringRockSoil02: e.target.value })}
                  className={`border border-gray-300 rounded px-2 ${ipy} w-full ${sm} outline-none`}>
                  <option value="">--</option>
                  {LAYERING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={`block ${lbl} text-gray-500 mb-1`}>Litologia Gruntu 3:</label>
                <select value={slot3Value} onChange={(e) => setSlot(3, e.target.value)}
                  className={`border border-gray-300 rounded px-2 ${ipy} w-full ${sm} outline-none`}
                  disabled={lithoDict.length === 0}>
                  <option value="">-- wybierz --</option>
                  {optionsWithCurrent(lithoDict, slot3Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {isRock && (
          <div className="space-y-2">
            {rockSlots.map((s, sIdx) => (
              <React.Fragment key={s.idx}>
                {sIdx > 0 && (
                  <div className="pl-2">
                    <label className={`block ${lbl} text-gray-500 mb-1`}>Przewarstwienie {sIdx} (między slotami):</label>
                    <select value={value[`layeringRockSoil0${sIdx}`] || ''} onChange={(e) => onChange({ [`layeringRockSoil0${sIdx}`]: e.target.value })}
                      className={`border border-gray-300 rounded px-2 ${ipy} w-24 ${sm} outline-none`}>
                      <option value="">--</option>
                      {LAYERING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                )}
                <div className="border border-gray-200 rounded p-2 space-y-1.5">
                  <div>
                    <label className={`block ${lbl} font-semibold text-gray-700 mb-1`}>Skała {s.idx}:</label>
                    <select value={s.rockValue} onChange={(e) => setRockPart(s.idx, e.target.value)}
                      className={`border border-gray-300 rounded px-2 ${ipy} w-full ${sm} outline-none`}
                      disabled={rockDict.length === 0}>
                      <option value="">{s.idx === 1 ? '-- wybierz --' : '-- brak --'}</option>
                      {optionsWithCurrent(rockDict, s.rockValue).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  {s.rockValue && (
                    <div className="space-y-1 pl-2 border-l-2 border-gray-100">
                      <div className={`${lbl} text-gray-500`}>Podkategoria w nawiasie (z subCatRockySoil, opcjonalne):</div>
                      {subDict.length === 0 && (
                        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          Słownik <code>subCatRockySoil</code> pusty — podkategorie niedostępne.
                        </div>
                      )}
                      <div>
                        <label className={`block ${lbl} text-gray-500 mb-1`}>Podkat. 1:</label>
                        <select value={s.sub1Value} onChange={(e) => setSubSlot(s.idx, 1, e.target.value)}
                          className={`border border-gray-300 rounded px-2 ${ipy} w-full ${sm} outline-none`}
                          disabled={subDict.length === 0}>
                          <option value="">-- brak --</option>
                          {optionsWithCurrent(subDict, s.sub1Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      {s.sub1Value && (
                        <div className="grid grid-cols-[70px_1fr] gap-2 items-end">
                          <div>
                            <label className={`block ${lbl} text-gray-500 mb-1`}>Sub-pr. 1:</label>
                            <select value={s.sep1} onChange={(e) => setSubSep(s.idx, 1, e.target.value)}
                              className={`border border-gray-300 rounded px-1 ${ipy} w-full ${sm} outline-none`}>
                              <option value="">--</option>
                              {LAYERING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className={`block ${lbl} text-gray-500 mb-1`}>Podkat. 2:</label>
                            <select value={s.sub2Value} onChange={(e) => setSubSlot(s.idx, 2, e.target.value)}
                              className={`border border-gray-300 rounded px-2 ${ipy} w-full ${sm} outline-none`}
                              disabled={subDict.length === 0}>
                              <option value="">-- brak --</option>
                              {optionsWithCurrent(subDict, s.sub2Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                      {s.sub2Value && (
                        <div className="grid grid-cols-[70px_1fr] gap-2 items-end">
                          <div>
                            <label className={`block ${lbl} text-gray-500 mb-1`}>Sub-pr. 2:</label>
                            <select value={s.sep2} onChange={(e) => setSubSep(s.idx, 2, e.target.value)}
                              className={`border border-gray-300 rounded px-1 ${ipy} w-full ${sm} outline-none`}>
                              <option value="">--</option>
                              {LAYERING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className={`block ${lbl} text-gray-500 mb-1`}>Podkat. 3:</label>
                            <select value={s.sub3Value} onChange={(e) => setSubSlot(s.idx, 3, e.target.value)}
                              className={`border border-gray-300 rounded px-2 ${ipy} w-full ${sm} outline-none`}
                              disabled={subDict.length === 0}>
                              <option value="">-- brak --</option>
                              {optionsWithCurrent(subDict, s.sub3Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {(lithoDict.length === 0 || (isRock && rockDict.length === 0)) && (
          <div className={`${lbl} text-amber-600 mt-2`}>
            Słownik niezaładowany (brak kolekcji w Firestore lub jeszcze się ładuje).
          </div>
        )}
      </div>
    </>
  );
}
